import {
    appAbilitiesSchema,
    CategoriaEnum,
    defineAbilitiesFor,
} from '@acme/auth'
import modules, { naoListadas, TSubmodule } from '@acme/auth/modules'
import { AppAbilities } from '@acme/auth/types'
import Cookies from 'js-cookie'
import jwt_decode from 'jwt-decode'
import React from 'react'
import { Menu } from 'routes/types'
import { TokenDecoded } from 'types'

const newTab = [
    '/ajuda/atendimento-online',
    '/ajuda/manual',
    '/relatorios/logs',
]

let cachedToken = ''
let cachedAbility = defineAbilitiesFor({
    categoriaId: CategoriaEnum.Publico,
    isAtivo: false,
})

/**
 * Função que decodifica o token JWT e retorna as habilidades do usuário para controle de acesso
 * A função utiliza cache para evitar decodificações desnecessárias, melhorando a performance.
 * Se o token for inválido ou ausente, retorna as habilidades de um usuário público.
 * @returns As habilidades do usuário decodificado ou do usuário público.
 */
export function getAbilityFromToken() {
    const token = Cookies.get('JWT')

    if (!token)
        return defineAbilitiesFor({
            categoriaId: CategoriaEnum.Publico,
            isAtivo: true,
        })

    if (cachedToken === token) return cachedAbility

    try {
        const decoded = jwt_decode<TokenDecoded>(token)
        const userAbilities = appAbilitiesSchema.safeParse(
            decoded.userAbilities,
        )

        cachedToken = token
        cachedAbility = defineAbilitiesFor({
            categoriaId: decoded.categoria,
            modulos: decoded.modulos,
            isAtivo: decoded.isAtivo,
            userAppAbilities: userAbilities.data || undefined,
        })

        return cachedAbility
    } catch (_) {
        return defineAbilitiesFor({
            categoriaId: CategoriaEnum.Publico,
            isAtivo: true,
        })
    }
}

export function useBackendMenu() {
    const permissions = getAbilityFromToken()

    if (!permissions) return []

    return Object.entries(modules)
        .map(([key, module]) => ({
            label: key,
            subitens: Object.values<TSubmodule>(module).map(v => ({
                label: v.label,
                subject: v.subject,
                showInMenu: v.showInMenu,
                redirectTo: v.href,
                newTab: newTab.includes(v.href),
            })),
        }))
        .map(item => ({
            ...item,
            subitens: item.subitens
                .filter(it => it.showInMenu)
                .filter(it => permissions.can('read', it.subject))
                .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .filter(item => item.subitens.length) as Menu
}

export function hasPathPermission(type: AppAbilities[0], path: string) {
    const permissions = getAbilityFromToken()
    const routes = Object.values(modules).flatMap(i =>
        Object.values<TSubmodule>(i),
    )
    const rotasNaoListadas = Object.values(naoListadas)

    return [...routes, ...rotasNaoListadas].some(it => {
        const { href, subject } = it
        return String(path).startsWith(href) && permissions.can(type, subject)
    })
}

/** Função que verifica se o usuário possui determinada permissão no sistema
 *
 * @example
 * ```tsx
 * // Verifica se o usuário possui permissão
 * const canUpdate = hasPermission(['update', 'Empresas'])
 *
 * // Renderiza um componente se o usuário possuir permissão
 * hasPermission(['update', 'Empresas'], () => (
 *     <button>Editar Empresa</button>
 * ))
 *
 * // Fallback caso o usuário não possua permissão
 * hasPermission(['update', 'Empresas'], () => (
 *     <button>Editar Empresa</button>
 * ), () => (
 *     <span>Sem permissão para editar</span>
 * ))
 * ```
 */
export function hasPermission(perm: AppAbilities): boolean
export function hasPermission(
    perm: AppAbilities,
    truth: () => React.ReactNode,
): React.ReactNode
export function hasPermission(
    perm: AppAbilities,
    truth: () => React.ReactNode,
    falsy: () => React.ReactNode,
): React.ReactNode
export function hasPermission(
    perm: AppAbilities,
    truth?: () => React.ReactNode,
    falsy?: () => React.ReactNode,
) {
    const permissions = getAbilityFromToken()
    if (!truth && !falsy) {
        return permissions.can(...(perm as any))
    }

    falsy ??= () => null
    return permissions.can(...(perm as any)) ? truth?.() : falsy()
}
